import { push } from "connected-react-router";
import "whatwg-fetch";
import {
  ERROR_NOTIFICATION,
  TOKEN_EXPIRED,
  UPDATE_TOKEN,
} from "../constants/ActionTypes";
import { NoTokenError } from "../utils";

const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

// Only call the CSRF cookie endpoint if the CSRF cookie is missing
export async function getCsrfCookie() {
  if (
    !document.cookie.split("; ").find((row) => row.startsWith("XSRF-TOKEN="))
  ) {
    return fetch(`${apiEndpoint}/sanctum/csrf-cookie`, {
      credentials: "include",
    });
  }
  // If already present, resolve immediately
  return Promise.resolve();
}

// Utility to get a cookie value by name
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const updateToken = (token) => {
  return {
    type: UPDATE_TOKEN,
    token,
  };
};

export function API(
  endpoint,
  method,
  data = null,
  contentType = "application/json",
  toJSON = true,
) {
  return async (dispatch, getState) => {
    let { token } = getState().auth;

    await getCsrfCookie();
    const xsrfToken = getCookie("XSRF-TOKEN");

    let config = {
      method: method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-CSRF-TOKEN": xsrfToken,
      },
      credentials: "include",
    };
    if (contentType) config.headers["Content-Type"] = contentType;

    if (method === "POST" || method === "PUT") {
      if (toJSON) config.body = JSON.stringify(data);
      else config.body = data;
    }

    return new Promise((resolve, reject) => {
      if (!token) {
        dispatch(push("/login"));
        reject(new NoTokenError("No token saved. Redirecting..."));
      }

      let url = `${apiEndpoint}${endpoint}`;
      fetch(
        constructParams(method === "GET" ? constructParams(url, data) : url),
        config,
      )
        .then(parseResponse)
        .then((res) => {
          let { result, ok, status } = res;
          if (ok) resolve(result);
          else {
            if (result.message === "Token has expired") {
              //If we have an expired token, first invalidate the store
              dispatch({ type: TOKEN_EXPIRED });

              //Now attempt to refresh the token
              refreshToken(token, dispatch)
                .then((newToken) => {
                  //if the token is refreshed, try the original API method again
                  console.log("New token received");
                  resolve(dispatch(API(endpoint, method, data)));
                })
                .catch(reject);
            } else if (status === 403) {
              sessionStorage.removeItem("token");
              sessionStorage.removeItem("roles");
              sessionStorage.removeItem("permissions");
              dispatch(push("/login"));
            } else {
              reject(result);
            }
          }
        })
        .catch((error) => {
          console.log("Dispatching error: ", error);
          dispatch({ type: ERROR_NOTIFICATION, error: error });
        });
    });
  };
}
function parseResponse(response) {
  let ok = response?.ok;
  let status = response?.status;
  return response.json().then((result) => ({ result, ok, status }));
}

export async function refreshToken(token, dispatch) {
  try {
    await getCsrfCookie();
    const xsrfToken = getCookie("XSRF-TOKEN");

    const response = await fetch(`${apiEndpoint}/auth/renew`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": xsrfToken,
      },
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 401) {
        dispatch(push("/login"));
      }
      throw new Error("Failed to refresh token");
    }

    const result = await response.json();
    sessionStorage.setItem("token", result.token);
    dispatch(updateToken(result.token));
    return result.token;
  } catch (error) {
    console.error("Error refreshing token:", error);
    dispatch({ type: ERROR_NOTIFICATION, error: error });
    throw error;
  }
}

function constructParams(url, parameters) {
  var qs = "";
  for (var key in parameters) {
    var value = parameters[key];
    qs += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
  }
  if (qs.length > 0) {
    qs = qs.substring(0, qs.length - 1); //chop off last "&"
    url = url + "?" + qs;
  }
  return url;
}
